import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PaginatedListComponent } from '../../../../shared/components/paginated-list/paginated-list.component';
import { Event } from '../../../../shared/model/event';
import { EventService } from '../../../../shared/services/event.service';
import { AlertService } from '../../../../shared/services/alert.service';
import { Messages } from '../../../../shared/constants/messages';
import { EventCardComponent } from './event-card/event-card.component';
import { PermissionService } from '../../../../core/services/permission.service';
import { sortEditableFirst } from '../../../../shared/utils/editable-first';
import { OsmMapComponent } from '../../../../shared/components/osm-map/osm-map.component';
import { countMappableEvents, eventMapMarkers } from '../../../../shared/utils/map-markers';
import { UserGeolocationService } from '../../../../shared/services/user-geolocation.service';
import {
  extractLocationDisplayName,
  matchesEventGlobalSearch,
  matchesOrganizationName,
  parseMaxDistanceKm,
  withinMaxDistanceKm,
} from '../../../../shared/utils/catalog-filters';
import { parseCoordinates } from '../../../../shared/utils/geo-coordinates';
import { compareEventsByStartDateAndDistance } from '../../../../shared/utils/event-sorting';
import {
  CatalogAdvancedFilterComponent,
  CatalogAdvancedFilterValues,
} from '../../../../shared/components/catalog-advanced-filter/catalog-advanced-filter.component';

@Component({
  selector: 'app-event-list',
  standalone: true,
  imports: [
    PaginatedListComponent,
    EventCardComponent,
    OsmMapComponent,
    ReactiveFormsModule,
    CatalogAdvancedFilterComponent,
  ],
  templateUrl: './event-list.component.html',
  styleUrl: './event-list.component.css',
})
export class EventListComponent implements OnInit {
  private eventService: EventService = inject(EventService);
  private alertService: AlertService = inject(AlertService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private permissionService: PermissionService = inject(PermissionService);
  private userGeolocation: UserGeolocationService = inject(UserGeolocationService);
  private destroyRef = inject(DestroyRef);

  protected events = signal<Array<Event>>([]);
  protected view = signal<'list' | 'map'>('list');
  protected isAdvancedSearch = signal<boolean>(false);
  protected includePast = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);

  protected searchControl = new FormControl<string>('', { nonNullable: true });
  protected searchQuery = signal<string>('');

  protected advancedFilters = signal<CatalogAdvancedFilterValues>({
    tableName: null,
    gameName: null,
    eventName: null,
    location: null,
    organization: null,
    maxDistanceKm: null,
  });

  protected readonly isMine = computed(() => this.route.snapshot.data['filter'] === 'mine');
  protected readonly canCreateEvent = this.permissionService.canCreateEvent();

  protected readonly visibleEvents = computed(() =>
    this.sortEvents(this.filterEvents(this.events())),
  );
  protected readonly mapMarkers = computed(() => eventMapMarkers(this.visibleEvents()));
  protected readonly mappableCount = computed(() => countMappableEvents(this.visibleEvents()));

  ngOnInit(): void {
    this.userGeolocation.ensureLocated();

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => this.searchQuery.set(query));

    this.loadEvents();
  }

  loadEvents(): void {
    this.isLoading.set(true);
    const events$ = this.isMine()
      ? this.eventService.getMyEvents()
      : this.eventService.getAllEvents(this.includePast());

    events$.subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.success && response.data) {
          const listed = this.isMine()
            ? response.data
            : response.data.filter((event) => event.active);
          this.events.set(listed);
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.alertService.showError(Messages.EVENT_LOAD_ERROR);
      },
    });
  }

  protected toggleAdvancedSearch(): void {
    this.isAdvancedSearch.update((current) => !current);
  }

  protected closeAdvancedSearch(): void {
    this.isAdvancedSearch.set(false);
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  protected onAdvancedFilterChange(values: CatalogAdvancedFilterValues): void {
    this.advancedFilters.set(values);
  }

  protected onIncludePastChange(includePast: boolean): void {
    this.includePast.set(includePast);
    if (!this.isMine()) {
      this.loadEvents();
    }
  }

  protected toggleIncludePast(): void {
    this.includePast.update((prev) => !prev);
    if (!this.isMine()) {
      this.loadEvents();
    }
  }

  protected onAdvancedFilterReset(): void {
    this.advancedFilters.set({
      tableName: null,
      gameName: null,
      eventName: null,
      location: null,
      organization: null,
      maxDistanceKm: null,
    });
    if (this.includePast()) {
      this.includePast.set(false);
      if (!this.isMine()) {
        this.loadEvents();
      }
    }
  }

  protected setView(view: 'list' | 'map'): void {
    this.view.set(view);
  }

  goToAdd(): void {
    this.router.navigate(['/events/add']);
  }

  private filterEvents(listed: Event[]): Event[] {
    if (this.isMine()) {
      return listed;
    }

    if (this.isAdvancedSearch()) {
      const filters = this.advancedFilters();
      const eventNameFilter = (filters.eventName ?? '').toLowerCase().trim();
      const locationFilter = (filters.location ?? '').toLowerCase().trim();
      const organizationFilter = filters.organization;
      const maxKm = parseMaxDistanceKm(filters.maxDistanceKm);
      const user = this.userGeolocation.position();

      return listed.filter((event) => {
        const eventName = (event.name ?? '').toLowerCase();
        const locationName = extractLocationDisplayName(event.location, '').toLowerCase();
        const organizationName = event.organization?.organizationName;

        return (
          (!eventNameFilter || eventName.includes(eventNameFilter)) &&
          (!locationFilter || locationName.includes(locationFilter)) &&
          matchesOrganizationName(organizationName, organizationFilter) &&
          withinMaxDistanceKm(
            user,
            parseCoordinates(event.location),
            maxKm,
          )
        );
      });
    }

    const query = this.searchQuery();
    return listed.filter((event) => matchesEventGlobalSearch(event, query));
  }

  private sortEvents(listed: Event[]): Event[] {
    const compare = compareEventsByStartDateAndDistance(this.userGeolocation.position());
    if (this.isMine()) {
      return sortEditableFirst(listed, (event) => event.isCreator, compare);
    }
    return [...listed].sort(compare);
  }
}
